import {
  ComponentRef,
  EmbeddedViewRef,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { SplitButtonComponent } from '../split-button';
import { CalendarActionButtonComponent } from './components/calendar-action-button.component';

export interface CalendarHeaderButtonsDeps {
  viewContainerRef: ViewContainerRef;
  renderer: Renderer2;
  onSplitButtonAction: (value: string) => void;
}

export interface CalendarHeaderButtonsFlags {
  extraHeaderTemplate: TemplateRef<unknown> | null;
  addPrestazioneButton: boolean;
  addPrestazioneTemplate: TemplateRef<unknown> | null;
  addNewEventButton: boolean;
  splitButtonOptions: string[];
  changeViewButton: boolean;
  changeViewTemplate: TemplateRef<unknown> | null;
}

/**
 * Owns the imperative DOM/ComponentRef wiring needed to inject Angular
 * components and templates (view toggle, add-prestazione, add-new, date
 * range, extra header) into FullCalendar's own toolbar, which only exposes
 * plain DOM button hosts and has no first-class Angular template support.
 * Instantiated (not DI-provided) by CalendarComponent, which is its only
 * consumer.
 */
export class CalendarHeaderButtonsController {
  private addPrestazioneButtonContainer: Element | null = null;
  private addNewSplitButton: ComponentRef<SplitButtonComponent> | null = null;
  private addNewActionButton: ComponentRef<CalendarActionButtonComponent> | null =
    null;
  private extraHeaderEmbeddedView: EmbeddedViewRef<unknown> | null = null;
  private addPrestazioneEmbeddedView: EmbeddedViewRef<unknown> | null = null;
  private changeViewEmbeddedView: EmbeddedViewRef<unknown> | null = null;

  constructor(private readonly deps: CalendarHeaderButtonsDeps) {}

  get hasAddPrestazioneButtonContainer(): boolean {
    return !!this.addPrestazioneButtonContainer;
  }

  /**
   * Syncs the already-injected add-new button/split-button with the latest
   * set of options (e.g. when the parent's [splitButtonOptions] input changes).
   */
  syncSplitButtonOptions(options: string[]): void {
    if (this.addNewSplitButton) {
      this.addNewSplitButton.setInput('options', options);
    }
    if (this.addNewActionButton && options.length === 1) {
      this.addNewActionButton.setInput('label', options[0]);
    }
  }

  injectAll(flags: CalendarHeaderButtonsFlags): void {
    if (flags.extraHeaderTemplate) {
      this.injectExtraHeaderTemplate(flags.extraHeaderTemplate);
    }
    if (flags.addPrestazioneButton) {
      this.injectAddPrestazioneButton(flags.addPrestazioneTemplate);
    }
    if (flags.addNewEventButton) {
      this.injectAddNewSplitButton(flags.splitButtonOptions);
    }
    if (flags.changeViewButton) {
      this.injectChangeViewMenu(flags.changeViewTemplate);
    }
  }

  /**
   * Injects an Angular template (view toggle, add-prestazione, add-new, date
   * range, extra header) into the FullCalendar toolbar.
   */
  injectExtraHeaderTemplate(template: TemplateRef<unknown> | null): void {
    const buttonHost = document.querySelector('.fc-extraHeader-button');
    if (!buttonHost || !template) return;

    this.removeExtraHeaderView();

    this.deps.renderer.setAttribute(buttonHost, 'tabindex', '-1');
    this.deps.renderer.setAttribute(buttonHost, 'aria-hidden', 'true');
    this.deps.renderer.setStyle(buttonHost, 'display', 'contents');

    const embeddedView =
      this.deps.viewContainerRef.createEmbeddedView(template);
    embeddedView.detectChanges();
    embeddedView.rootNodes.forEach(node => {
      this.deps.renderer.appendChild(buttonHost, node);
    });

    this.extraHeaderEmbeddedView = embeddedView;
  }

  /**
   * Removes the already-injected extra header template.
   */
  removeExtraHeaderView(): void {
    if (this.extraHeaderEmbeddedView) {
      this.extraHeaderEmbeddedView.rootNodes.forEach(
        (node: Node & { remove?: () => void }) => node.remove?.()
      );
      this.extraHeaderEmbeddedView.destroy();
      this.extraHeaderEmbeddedView = null;
    }
  }

  /**
   * Removes the already-injected add-new button/split-button.
   */
  removeAddNewEventButtonComponents(): void {
    if (this.addNewSplitButton) {
      this.addNewSplitButton.location.nativeElement?.remove();
      this.addNewSplitButton.destroy();
      this.addNewSplitButton = null;
    }
    if (this.addNewActionButton) {
      this.addNewActionButton.location.nativeElement?.remove();
      this.addNewActionButton.destroy();
      this.addNewActionButton = null;
    }
  }

  /**
   * Injects an Angular component (split-button) into the FullCalendar toolbar.
   */
  injectAddNewSplitButton(options: string[]): void {
    const buttonHost = document.querySelector('.fc-addButton-button');
    if (!buttonHost) return;

    this.removeAddNewEventButtonComponents();

    this.deps.renderer.setStyle(buttonHost, 'display', 'contents');

    if (options.length === 1) {
      this.addNewActionButton = this.deps.viewContainerRef.createComponent(
        CalendarActionButtonComponent
      );
      this.addNewActionButton.setInput('label', options[0]);
      this.addNewActionButton.setInput(
        'tooltip',
        'Crea un nuovo evento nel calendario'
      );

      outputToObservable(this.addNewActionButton.instance.clicked).subscribe(
        (value: string) => this.deps.onSplitButtonAction(value)
      );

      this.deps.renderer.appendChild(
        buttonHost,
        this.addNewActionButton.location.nativeElement
      );
    } else {
      this.addNewSplitButton =
        this.deps.viewContainerRef.createComponent(SplitButtonComponent);
      this.addNewSplitButton.setInput('containerClass', 'calendar-split-button');
      this.addNewSplitButton.setInput('label', 'Nuovo');
      this.addNewSplitButton.setInput('icon', 'add');
      this.addNewSplitButton.setInput(
        'tooltip',
        'Crea un nuovo evento nel calendario'
      );
      this.addNewSplitButton.setInput('options', options);

      outputToObservable(
        this.addNewSplitButton.instance.onChangeEvent
      ).subscribe((value: string) => this.deps.onSplitButtonAction(value));

      this.deps.renderer.appendChild(
        buttonHost,
        this.addNewSplitButton.location.nativeElement
      );
    }
  }

  /**
   * Injects an Angular template (add-prestazione) into the FullCalendar toolbar.
   */
  injectAddPrestazioneButton(template: TemplateRef<unknown> | null): void {
    const buttonHost = document.querySelector(
      '.fc-addPrestazioneButton-button'
    );
    if (!buttonHost || !template) return;

    this.removeAddPrestazioneView();

    this.deps.renderer.setStyle(buttonHost, 'display', 'contents');
    this.addPrestazioneButtonContainer = buttonHost;

    const embeddedView =
      this.deps.viewContainerRef.createEmbeddedView(template);
    embeddedView.detectChanges();
    embeddedView.rootNodes.forEach(node => {
      this.deps.renderer.appendChild(buttonHost, node);
    });

    this.addPrestazioneEmbeddedView = embeddedView;
  }

  /**
   * Removes the already-injected add-prestazione template.
   */
  removeAddPrestazioneView(): void {
    if (this.addPrestazioneEmbeddedView) {
      this.addPrestazioneEmbeddedView.rootNodes.forEach(
        (node: Node & { remove?: () => void }) => node.remove?.()
      );
      this.addPrestazioneEmbeddedView.destroy();
      this.addPrestazioneEmbeddedView = null;
    }
  }

  /**
   * Removes the already-injected add-prestazione template.
   */
  clearAddPrestazioneButtonContainer(): void {
    if (!this.addPrestazioneButtonContainer) return;
    this.removeAddPrestazioneView();
    this.addPrestazioneButtonContainer.remove();
    this.addPrestazioneButtonContainer = null;
  }

  /**
   * Injects an Angular template (view toggle) into the FullCalendar toolbar.
   */
  injectChangeViewMenu(template: TemplateRef<unknown> | null): void {
    const buttonHost = document.querySelector('.fc-changeView-button');
    if (!buttonHost || !template) return;

    this.removeChangeViewView();

    this.deps.renderer.setStyle(buttonHost, 'display', 'contents');

    const embeddedView =
      this.deps.viewContainerRef.createEmbeddedView(template);
    embeddedView.detectChanges();
    embeddedView.rootNodes.forEach(node => {
      this.deps.renderer.appendChild(buttonHost, node);
    });

    this.changeViewEmbeddedView = embeddedView;
  }

  /**
   * When the calendar view is changed, remove the already-injected view toggle template.
   */
  removeChangeViewView(): void {
    if (this.changeViewEmbeddedView) {
      this.changeViewEmbeddedView.rootNodes.forEach(
        (node: Node & { remove?: () => void }) => node.remove?.()
      );
      this.changeViewEmbeddedView.destroy();
      this.changeViewEmbeddedView = null;
    }
  }

  /**
   * Injects an Angular template (date range) into the FullCalendar toolbar.
   */
  injectDateRangePicker(template: TemplateRef<unknown> | null): void {
    const buttonHost = document.querySelector('.fc-dateRange-button');
    if (!buttonHost || !template) return;

    this.deps.renderer.setProperty(buttonHost, 'innerHTML', '');
    const embeddedView =
      this.deps.viewContainerRef.createEmbeddedView(template);
    embeddedView.detectChanges();
    embeddedView.rootNodes.forEach(node => {
      this.deps.renderer.appendChild(buttonHost, node);
    });
  }
}
